import { Link } from "wouter";
import {
  useGetMember,
  useListCheckins,
  useGetSquad,
  useListSchedules,
  useCreateScheduleEntry,
  useUpdateScheduleEntry,
  useDeleteScheduleEntry,
  getGetMemberQueryKey,
  getListCheckinsQueryKey,
  getListSchedulesQueryKey,
} from "@workspace/api-client-react";
import type { CreateScheduleEntryBodyDayOfWeek } from "@workspace/api-client-react";
import type { ScheduleEntry } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useCurrentMember } from "@/hooks/useCurrentMember";
import { localDateStr, addLocalDays } from "@/lib/localDate";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SESSION_TYPES = [
  "Easy Run",
  "Long Run",
  "Tempo",
  "Interval",
  "Fartlek",
  "Recovery",
  "Cross-Train",
  "Rest",
  "Race",
  "Strength",
  "Other",
];

export function CheckinHeatmap({ checkins }: { checkins: Array<{ checkinDate: string; status: string }> }) {
  const weeks = 12;
  const days = 7;
  // WI#22 — real Monday-to-Sunday weeks, so each row is the weekday its label says. Before, each
  // column was "the 7 days ending today", which put today on the Sun row whatever the day was.
  const today = new Date();
  const todayStr = localDateStr(today);
  const thisMonday = addLocalDays(today, -((today.getDay() + 6) % 7));
  const firstMonday = addLocalDays(thisMonday, -(weeks - 1) * 7);
  const cells: Array<{ date: string; status: string | null; future: boolean }> = [];

  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < days; d++) {
      const dateStr = localDateStr(addLocalDays(firstMonday, w * 7 + d));
      const checkin = checkins.find((c) => c.checkinDate === dateStr);
      cells.push({ date: dateStr, status: checkin?.status ?? null, future: dateStr > todayStr });
    }
  }

  const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div>
      <div className="flex gap-1 mb-1.5">
        <div className="w-8 flex-shrink-0" />
        {Array.from({ length: weeks }).map((_, w) => {
          const date = addLocalDays(firstMonday, w * 7);
          return (
            <div key={w} className="flex-1 text-center text-xs font-mono text-muted-foreground">
              {w % 3 === 0 ? date.toLocaleDateString("en-US", { month: "short" }) : ""}
            </div>
          );
        })}
      </div>
      <div className="flex gap-1">
        <div className="flex flex-col gap-1 w-8 flex-shrink-0">
          {dayLabels.map((d, i) => (
            <div key={i} className="h-5 text-xs font-mono text-muted-foreground flex items-center">
              {i % 2 === 0 ? d : ""}
            </div>
          ))}
        </div>
        <div className="flex gap-1 flex-1">
          {Array.from({ length: weeks }).map((_, w) => (
            <div key={w} className="flex flex-col gap-1 flex-1">
              {Array.from({ length: days }).map((_, d) => {
                const cell = cells[w * days + d];
                return (
                  <div
                    key={d}
                    title={cell?.future ? cell.date : `${cell?.date}: ${cell?.status ?? "no data"}`}
                    className={cn(
                      "h-5 w-full rounded-sm",
                      cell?.future
                        ? "bg-transparent border border-dashed border-border"
                        : cell?.status === "crushed"
                        ? "bg-primary"
                        : cell?.status === "skipped"
                        ? "bg-red-500"
                        : "bg-zinc-200"
                    )}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-5 mt-3 pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-primary" />
          <span className="text-xs text-foreground font-mono font-medium">Crushed it</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-red-500" />
          <span className="text-xs text-foreground font-mono font-medium">Skipped</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-zinc-200" />
          <span className="text-xs text-muted-foreground font-mono">No data</span>
        </div>
      </div>
    </div>
  );
}

function SessionModal({
  memberId,
  squadId,
  entry,
  onClose,
}: {
  memberId: number;
  squadId: number;
  entry?: ScheduleEntry;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const createEntry = useCreateScheduleEntry();
  const updateEntry = useUpdateScheduleEntry();

  const [weekNumber, setWeekNumber] = useState(String(entry?.weekNumber ?? 1));
  const [dayOfWeek, setDayOfWeek] = useState(entry?.dayOfWeek ?? "Monday");
  const [sessionType, setSessionType] = useState(entry?.sessionType ?? "Easy Run");
  const [targetMiles, setTargetMiles] = useState(entry?.targetMiles != null ? String(entry.targetMiles) : "");
  const [description, setDescription] = useState(entry?.description ?? "");
  const [error, setError] = useState("");

  const isEdit = !!entry;

  const scheduleQueryKey = getListSchedulesQueryKey({ memberId });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (isEdit) {
      updateEntry.mutate(
        {
          id: entry.id,
          data: {
            weekNumber: Number(weekNumber),
            dayOfWeek: dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
            sessionType,
            targetMiles: targetMiles ? Number(targetMiles) : null,
            description: description.trim() || null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
            onClose();
          },
          onError: () => setError("Failed to save session. Please try again."),
        }
      );
    } else {
      createEntry.mutate(
        {
          data: {
            squadId,
            memberId,
            weekNumber: Number(weekNumber),
            dayOfWeek: dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
            sessionType,
            targetMiles: targetMiles ? Number(targetMiles) : null,
            description: description.trim() || null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
            onClose();
          },
          onError: () => setError("Failed to create session. Please try again."),
        }
      );
    }
  };

  const isPending = createEntry.isPending || updateEntry.isPending;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-6">
          {isEdit ? "Edit Session" : "Add Session"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pr-label">
                Week #
              </label>
              <input
                type="number"
                value={weekNumber}
                onChange={(e) => setWeekNumber(e.target.value)}
                min={1}
                className="pr-field"
              />
            </div>
            <div>
              <label className="pr-label">
                Day
              </label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(e.target.value)}
                className="pr-field"
              >
                {DAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="pr-label">
              Session Type
            </label>
            <select
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              className="pr-field"
            >
              {SESSION_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="pr-label">
              Target Miles
            </label>
            <input
              type="number"
              value={targetMiles}
              onChange={(e) => setTargetMiles(e.target.value)}
              placeholder="e.g. 8"
              min={0}
              step={0.5}
              className="pr-field"
            />
          </div>
          <div>
            <label className="pr-label">
              Notes
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes, pace targets, etc."
              rows={2}
              className="pr-field"
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!weekNumber || isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Session"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function CopySessionModal({
  entry,
  memberId,
  squadId,
  existingWeeks,
  onClose,
  onSuccess,
}: {
  entry: ScheduleEntry;
  memberId: number;
  squadId: number;
  existingWeeks: number[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const createEntry = useCreateScheduleEntry();
  const [targetWeek, setTargetWeek] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const week = Number(targetWeek);
    if (!week || week < 1) {
      setError("Enter a valid week number.");
      return;
    }
    setError("");
    createEntry.mutate(
      {
        data: {
          squadId,
          memberId,
          weekNumber: week,
          dayOfWeek: entry.dayOfWeek as CreateScheduleEntryBodyDayOfWeek,
          sessionType: entry.sessionType,
          targetMiles: entry.targetMiles ?? null,
          description: entry.description ?? null,
        },
      },
      {
        onSuccess: () => { onSuccess(); onClose(); },
        onError: () => setError("Failed to copy session. Please try again."),
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-2">Copy Session</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Copying{" "}
          <span className="font-semibold text-foreground">{entry.sessionType}</span>{" "}
          on{" "}
          <span className="font-semibold text-foreground">{entry.dayOfWeek}</span>
          {entry.targetMiles ? ` · ${entry.targetMiles}mi` : ""} to another week.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="pr-label">
              Target Week
            </label>
            <input
              type="number"
              value={targetWeek}
              onChange={(e) => setTargetWeek(e.target.value)}
              placeholder="e.g. 3"
              min={1}
              autoFocus
              className="pr-field"
            />
            {existingWeeks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {existingWeeks.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setTargetWeek(String(w))}
                    className={cn(
                      "px-2 py-0.5 rounded text-xs font-mono font-bold transition-colors border",
                      String(w) === targetWeek
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Wk {w}
                  </button>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!targetWeek || createEntry.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {createEntry.isPending ? "Copying..." : "Copy Session"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ScheduleTab({
  memberId,
  squadId,
  canEdit,
}: {
  memberId: number;
  // WI#16 (S1): null for a runner who is on no roster. The schedule belongs to the member, not
  // the squad, so it stays viewable and editable — the squad is only used to scope queries.
  squadId: number | null;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const deleteEntry = useDeleteScheduleEntry();
  const { data: schedules, isLoading } = useListSchedules(
    { memberId },
    { query: { queryKey: getListSchedulesQueryKey({ memberId }) } }
  );

  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ScheduleEntry | undefined>(undefined);
  const [copyingEntry, setCopyingEntry] = useState<ScheduleEntry | undefined>(undefined);
  const [deleteError, setDeleteError] = useState("");

  const scheduleQueryKey = getListSchedulesQueryKey({ memberId });

  const weekGroups: Record<number, ScheduleEntry[]> = {};
  schedules?.forEach((s) => {
    if (!weekGroups[s.weekNumber]) weekGroups[s.weekNumber] = [];
    weekGroups[s.weekNumber]!.push(s);
  });

  const handleDelete = (entry: ScheduleEntry) => {
    setDeleteError("");
    deleteEntry.mutate(
      { id: entry.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
        },
        onError: () => setDeleteError("Failed to delete session."),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="text-muted-foreground font-mono text-sm animate-pulse">Loading schedule...</span>
      </div>
    );
  }

  return (
    <div>
      {showModal && squadId !== null && (
        <SessionModal
          memberId={memberId}
          squadId={squadId}
          entry={editingEntry}
          onClose={() => {
            setShowModal(false);
            setEditingEntry(undefined);
          }}
        />
      )}
      {copyingEntry && squadId !== null && (
        <CopySessionModal
          entry={copyingEntry}
          memberId={memberId}
          squadId={squadId}
          existingWeeks={Object.keys(weekGroups).map(Number).sort((a, b) => a - b)}
          onClose={() => setCopyingEntry(undefined)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: scheduleQueryKey })}
        />
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {schedules?.length === 0
            ? "No sessions yet."
            : `${schedules?.length} session${schedules?.length === 1 ? "" : "s"} planned`}
        </p>
        {canEdit && (
          <button
            onClick={() => {
              setEditingEntry(undefined);
              setShowModal(true);
            }}
            className="text-xs font-semibold text-foreground underline underline-offset-2 hover:text-muted-foreground"
          >
            + Add Session
          </button>
        )}
      </div>

      {deleteError && <p className="text-destructive text-sm mb-3">{deleteError}</p>}

      {Object.keys(weekGroups).length === 0 ? (
        <div className="border border-dashed border-border rounded p-10 text-center">
          <p className="text-muted-foreground text-sm mb-2">No training sessions yet.</p>
          {canEdit && (
            <button
              onClick={() => {
                setEditingEntry(undefined);
                setShowModal(true);
              }}
              className="text-sm font-semibold text-foreground underline underline-offset-2 hover:text-muted-foreground"
            >
              Add your first session
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(weekGroups)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([week, entries]) => (
              <div key={week} className="border border-border rounded overflow-hidden">
                <div className="bg-zinc-100 px-4 py-2.5 border-b border-border">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-foreground">
                    Week {week}
                  </span>
                  <span className="text-xs font-mono text-muted-foreground ml-3">
                    {entries.filter((e) => e.sessionType.toLowerCase() !== "rest").length} sessions
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-[repeat(7,1fr)] min-w-[560px]">
                    {DAYS.map((day) => (
                      <div key={day} className="border-r border-border last:border-r-0 px-2 py-2 text-center border-b">
                        <span className="text-xs font-mono font-semibold text-foreground uppercase">
                          {day.slice(0, 3)}
                        </span>
                      </div>
                    ))}
                    {DAYS.map((day) => {
                      const entry = entries.find((e) => e.dayOfWeek === day);
                      const isRest = !entry || entry.sessionType.toLowerCase() === "rest";
                      return (
                        <div
                          key={day}
                          className="border-r border-border last:border-r-0 p-2 min-h-[80px] group relative"
                        >
                          {!isRest && entry ? (
                            <div>
                              <div className="text-xs font-semibold text-foreground leading-tight mb-1.5">
                                {entry.sessionType}
                              </div>
                              {entry.targetMiles && (
                                <span className="inline-block bg-primary text-primary-foreground text-xs font-mono font-bold px-1.5 py-0.5 rounded-sm mb-1.5">
                                  {entry.targetMiles}mi
                                </span>
                              )}
                              {entry.description && (
                                <p className="text-xs text-muted-foreground leading-tight">
                                  {entry.description}
                                </p>
                              )}
                              {canEdit && (
                                <div className="flex gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => {
                                      setEditingEntry(entry);
                                      setShowModal(true);
                                    }}
                                    className="text-xs text-muted-foreground hover:text-foreground font-mono"
                                  >
                                    edit
                                  </button>
                                  <span className="text-muted-foreground text-xs">·</span>
                                  <button
                                    onClick={() => setCopyingEntry(entry)}
                                    className="text-xs text-muted-foreground hover:text-foreground font-mono"
                                  >
                                    copy
                                  </button>
                                  <span className="text-muted-foreground text-xs">·</span>
                                  <button
                                    onClick={() => handleDelete(entry)}
                                    disabled={deleteEntry.isPending}
                                    className="text-xs text-destructive/70 hover:text-destructive font-mono disabled:opacity-50"
                                  >
                                    del
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">Rest</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function MemberDetail({ memberId }: { memberId: number }) {
  const { data: member, isLoading, isError } = useGetMember(memberId, {
    query: { enabled: !!memberId, queryKey: getGetMemberQueryKey(memberId) },
  });
  const { data: checkins } = useListCheckins(
    { memberId, limit: 100 },
    { query: { enabled: !!memberId, queryKey: getListCheckinsQueryKey({ memberId, limit: 100 }) } }
  );
  const { data: squad } = useGetSquad(member?.squadId ?? 0, {
    query: { enabled: !!member?.squadId, queryKey: [["squads", member?.squadId]] },
  });

  // WI#16 (S1): squadId is nullable now. null and undefined mean the same thing to this hook —
  // "no squad to resolve a membership in" — so it is normalised rather than widening the hook.
  const { member: currentMember, isCaptain, isAdmin } = useCurrentMember(member?.squadId ?? undefined);
  const isOwnProfile = currentMember?.id === memberId;
  const canViewSchedule = isAdmin || isCaptain || isOwnProfile;
  const canEditSchedule = isAdmin || isCaptain || isOwnProfile;

  const [activeTab, setActiveTab] = useState<"overview" | "schedule">("overview");

  if (isLoading) {
    return (
      <div className="animate-pulse">
        {/* Breadcrumb skeleton */}
        <div className="h-3 bg-muted rounded w-20 mb-8" />

        {/* Header skeleton */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="h-8 bg-muted rounded w-48 mb-2" />
            <div className="h-3 bg-muted rounded w-28 mb-1" />
            <div className="h-3 bg-muted rounded w-36" />
          </div>
          <div className="h-9 bg-muted rounded w-28" />
        </div>

        {/* Stats grid skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card p-5">
              <div className="h-8 bg-muted rounded w-16 mb-2" />
              <div className="h-3 bg-muted rounded w-24" />
            </div>
          ))}
        </div>

        {/* Heatmap section skeleton */}
        <div className="border border-border rounded p-6 mb-6">
          <div className="h-4 bg-muted rounded w-48 mb-4" />
          <div className="grid grid-cols-12 gap-1">
            {[...Array(84)].map((_, i) => (
              <div key={i} className="h-5 bg-muted rounded-sm" />
            ))}
          </div>
        </div>

        {/* Recent check-ins skeleton */}
        <div className="border border-border rounded">
          <div className="px-5 py-4 border-b border-border">
            <div className="h-4 bg-muted rounded w-32" />
          </div>
          <div className="divide-y divide-border">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-muted flex-shrink-0" />
                <div className="h-3 bg-muted rounded w-24" />
                <div className="h-3 bg-muted rounded w-16 ml-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="text-destructive font-mono text-sm font-medium">Unable to load runner</div>
        <div className="text-muted-foreground text-xs text-center max-w-xs">
          There was a problem fetching your data. Check your connection or try refreshing the page.
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="text-center py-24">
        <p className="text-muted-foreground">Runner not found.</p>
        <Link href="/members" className="text-muted-foreground text-sm mt-2 inline-block hover:text-foreground underline">
          Back to Members
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-8">
        <Link href="/members" className="text-muted-foreground text-sm hover:text-foreground transition-colors">
          Runners
        </Link>
        <span className="text-muted-foreground text-sm">/</span>
      </div>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-3xl font-bold">{member.name}</h1>
          </div>
          {squad && (
            <Link href={`/squads/${squad.id}`} className="text-muted-foreground text-sm hover:text-foreground transition-colors">
              {squad.name}
            </Link>
          )}
          {member.goalRace && (
            <p className="text-muted-foreground text-sm font-mono mt-0.5">{member.goalRace}</p>
          )}
        </div>
        {member.squadId !== null && (
          // WI#16 (S1): a null squadId interpolates to the string "null" and produces
          // /squads/null/checkin — a dead link. Types cannot catch this inside a template
          // literal, so it is guarded explicitly. The solo runner's own check-in entry point
          // is S4; hiding a broken button is the honest interim state.
          <Link
            href={`/squads/${member.squadId}/checkin`}
            className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Log Check-in
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mb-8">
        {[
          { label: "Current Streak", value: `${member.currentStreak}d` },
          { label: "Longest Streak", value: `${member.longestStreak}d` },
          { label: "Adherence", value: `${member.adherencePct}%` },
          { label: "Weekly Goal", value: member.weeklyMileageGoal ? `${member.weeklyMileageGoal}mi` : "—" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="bg-card p-5"
          >
            <div className="font-display text-3xl font-bold mb-0.5 text-foreground">
              {stat.value}
            </div>
            <div className="text-muted-foreground text-xs uppercase tracking-wider font-mono">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {canViewSchedule && (
        <div className="flex border-b border-border mb-6">
          <button
            onClick={() => setActiveTab("overview")}
            className={cn(
              "px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px",
              activeTab === "overview"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("schedule")}
            className={cn(
              "px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px",
              activeTab === "schedule"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Schedule
          </button>
        </div>
      )}

      {activeTab === "schedule" && canViewSchedule ? (
        <ScheduleTab
          memberId={memberId}
          squadId={member.squadId}
          canEdit={canEditSchedule}
        />
      ) : (
        <>
          {checkins && (
            <div className="border border-border rounded p-6 mb-6">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Check-in History (12 weeks)
              </h2>
              <CheckinHeatmap checkins={checkins} />
            </div>
          )}

          <div className="border border-border rounded">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Recent Check-ins
              </h2>
            </div>
            <div className="divide-y divide-border">
              {checkins?.length === 0 ? (
                <div className="px-5 py-8 text-center text-muted-foreground text-sm">No check-ins yet.</div>
              ) : (
                checkins?.slice(0, 20).map((checkin, i) => (
                  <motion.div
                    key={checkin.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="px-5 py-3 flex items-center gap-3"
                  >
                    <span
                      className={cn(
                        "w-2 h-2 rounded-full flex-shrink-0",
                        checkin.status === "crushed" ? "bg-primary" : "bg-muted-foreground/40"
                      )}
                    />
                    <span className="font-mono text-sm text-muted-foreground flex-shrink-0">{checkin.checkinDate}</span>
                    <span className={cn(
                      "text-sm font-medium",
                      checkin.status === "crushed" ? "text-foreground" : "text-muted-foreground"
                    )}>
                      {checkin.status === "crushed" ? "Crushed it" : "Skipped"}
                    </span>
                    {checkin.note && (
                      <span className="text-muted-foreground text-sm italic">— {checkin.note}</span>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
